import { useMemo } from 'react';
import type { GenerationAsset } from '../state/types';
import { useBrandLogos } from './useBrandLogos';

/**
 * 選択中素材からブランド (ロゴ / タグライン / 説明) を解決するフック。
 *
 * GenerationWorkspace から切り出したもので、ロジックは移設のみ (挙動不変)。
 * ASIN を持つ素材は ASIN 経由、持たない素材は素材ID経由 (マニュアル商品 /
 * 素材⇔ブランド直接リンク) で `/brands/by-asins` に問い合わせる。
 */
export function useSelectedAssetBrands(selectedAssets: GenerationAsset[]) {
  // selectedAssetsからASINを抽出してブランドロゴを取得
  const extractedAsins = useMemo(() => {
    const asinSet = new Set<string>();
    selectedAssets.forEach((asset) => {
      if (asset.asin) {
        if (Array.isArray(asset.asin)) {
          asset.asin.forEach((a: string) => {
            if (a) asinSet.add(a.toUpperCase());
          });
        } else if (typeof asset.asin === 'string') {
          asinSet.add(asset.asin.toUpperCase());
        }
      }
    });
    return Array.from(asinSet);
  }, [selectedAssets]);

  // 選択アセットに商品紐づきがあるか
  const hasAnyProductLinks = useMemo(() => {
    return selectedAssets.some(a => a.hasProductLinks || !!a.asin);
  }, [selectedAssets]);

  // ASIN以外の素材IDを抽出（マニュアル商品/素材⇔ブランド直接リンク経由のブランド取得用）
  const brandResolveMaterialIds = useMemo(() => {
    return selectedAssets
      .filter((a) => !a.asin)
      .map((a) => a.id);
  }, [selectedAssets]);

  const brandLogosQuery = useBrandLogos(extractedAsins, { materialIds: brandResolveMaterialIds });

  // 選択素材から説明つきブランドが解決できるか（商品説明が無くてもテキスト自動生成を許可する材料）
  const brandDescriptionAvailable = useMemo(() => {
    return (brandLogosQuery.data ?? []).some((brand) => (brand.description ?? '').trim().length > 0);
  }, [brandLogosQuery.data]);

  // ブランドロゴが利用可能かどうか
  const brandLogosAvailable = useMemo(() => {
    if (!brandLogosQuery.data) return false;
    return brandLogosQuery.data.some((brand) => brand.logos && brand.logos.length > 0);
  }, [brandLogosQuery.data]);

  // ブランドのタグラインを取得
  const brandTaglines = useMemo(() => {
    if (!brandLogosQuery.data) return [];
    return brandLogosQuery.data
      .filter((brand) => brand.tagline)
      .map((brand) => ({ brandName: brand.brand_name, tagline: brand.tagline! }));
  }, [brandLogosQuery.data]);

  // ロゴが設定されていないブランドを取得
  const brandsWithoutLogos = useMemo(() => {
    if (!brandLogosQuery.data) return [];
    return brandLogosQuery.data.filter((brand) => !brand.logos || brand.logos.length === 0);
  }, [brandLogosQuery.data]);

  return {
    extractedAsins,
    hasAnyProductLinks,
    brandLogosQuery,
    brandDescriptionAvailable,
    brandLogosAvailable,
    brandTaglines,
    brandsWithoutLogos,
  };
}
